// useAdmin — the canonical "am I a signed-in admin?" hook. Wraps the
// auth context + the /api/admin/me query into one ergonomic call so
// every page that needs gating just calls `const admin = useAdmin()`
// and reads `admin.state`.
//
// Four states a UI can branch on:
//   - 'loading'         — auth bootstrap or me-ping in flight; render blank
//   - 'signed_out'      — no token at all; show sign-in CTA
//   - 'not_authorized'  — signed in but not on ADMIN_EMAILS; show notice
//   - 'authorized'      — go ahead and render the page (email available)
//
// Centralizing here means a future change (e.g. add an "admin role" beyond
// the email allow-list) lands in one place instead of in every page.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from './auth-context';
import { getAdminMe, type AdminMe } from '@/api/admin';
import { ApiError } from '@/api/client';

export type AdminState =
  | { state: 'loading' }
  | { state: 'signed_out' }
  | { state: 'not_authorized' }
  | { state: 'error'; error: ApiError }
  | { state: 'authorized'; me: AdminMe };

export function useAdmin(): AdminState & {
  signIn: () => Promise<void>;
  signOut: () => void;
} {
  const auth = useAuth();

  const me = useQuery<AdminMe, ApiError>({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => getAdminMe(signal),
    enabled: !!auth.idToken,
    retry: false,
    // The me-ping result is the gate for the entire app; cache it for
    // the session. Manual refresh after sign-in is automatic via the
    // queryKey changing when AuthCallbackPage's reload re-mounts.
    staleTime: 5 * 60 * 1000,
  });

  const common = {
    signIn: () => auth.signIn(),
    signOut: () => auth.signOut(),
  };

  if (auth.loading) return { state: 'loading', ...common };
  if (!auth.idToken) return { state: 'signed_out', ...common };
  if (me.isLoading) return { state: 'loading', ...common };
  if (me.isError) {
    const status = me.error.status;
    if (status === 401 || status === 403) {
      return { state: 'not_authorized', ...common };
    }
    return { state: 'error', error: me.error, ...common };
  }
  if (!me.data) return { state: 'loading', ...common };
  return { state: 'authorized', me: me.data, ...common };
}
